import React, { FC, useEffect, useRef, useState } from 'react';
import { Product, Variant } from 'shared/types/product';
import { discardRepeatedValues } from '../product-details/helpers/discardRepeatedValues';

interface ColorSwatchProps {
  variants: Variant[];
  product?: Product;
  setSelectedVariant?: (variant: Variant) => void;
  viewMode?: string;
}

const ColorSwatchesDropdown: FC<ColorSwatchProps> = ({ product, variants, setSelectedVariant, viewMode }) => {
  const [variantsToUse, setVariantsToUse] = useState<Variant[]>([]);
  const [selectedVariant, setSelected] = useState<Variant | null>(
    product?.selectedVariant ?? null
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDisabled, setIsDisabled] = useState(false);


  // Helper function to get variant name
  const getVariantName = (variant: Variant | null) => {
    
    if (!variant?.attributes) return null;
    
    // If variant-name exists, use it (this will now work since we're fetching it)
    if (variant.attributes['variant-name']) {
      return variant.attributes['variant-name'];
    }
    
    // Fallback: create variant-name from available data
    const colour = variant.attributes.colour;
    const itemCode = variant.attributes['item-code'];
    
    if (colour && itemCode) {
      return `${colour} - ${itemCode}`;
    } else if (colour) {
      return colour;
    } else if (itemCode) {
      return itemCode;
    }
    
    return null;
  };



  useEffect(() => {
    // on existing plp page showing only unique colors

    const filteredVariants = variants
      //.filter(variant => variant?.attributes?.colour);
      .filter(variant => getVariantName(variant));
    setVariantsToUse(filteredVariants);

    if (filteredVariants.length <= 1) {
      setIsDisabled(true);
    }
  }, [variants]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (variant: Variant) => {
    setSelected(variant);
    if (setSelectedVariant) {
      setSelectedVariant(variant);
    }
    setIsOpen(false);
  };

  return (
    <div
      className={`relative ${viewMode == 'list' ? 'max-w-[250px]' : ''}`}
      ref={dropdownRef}
    >
      {getVariantName(selectedVariant) && (
        <div
          className={`flex items-center gap-5 p-4 border rounded-md bg-white shadow-sm relative ${!isDisabled ? "cursor-pointer" : "cursor-default"} w-130`}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
        >
          <img
            // src={`/images/colorswatches/swatch_${getVariantName(selectedVariant).replaceAll(" ", "").replaceAll("/", "")}.png`}
            src={`/images/colorswatches/swatch_${selectedVariant?.attributes?.colour.replaceAll(' ', '').replaceAll('/', '')}.png`}
            alt={getVariantName(selectedVariant)}
            className="w-16 h-16 rounded-full mr-2"
          />
          
          <div className="flex items-center justify-between w-full gap-2">
            <span className="text-gray-700 text-12 break-words whitespace-normal">
              {getVariantName(selectedVariant) || "Select a Color"}
            </span>

            {!isDisabled && (
              <svg
                className="w-18 h-18 flex-shrink-0 transition text-gray-600 cursor-pointer"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>

        </div>
      )}

      {isOpen && (
        <div
          className="absolute left-0 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50"
          style={{ position: "absolute" }}
        >
          {variantsToUse.map((variant) => (
            <div
              key={variant.id}
              className={`flex items-center p-2 gap-5 cursor-pointer ${
                selectedVariant?.id === variant.id
                  ? "bg-blue-100 font-semibold"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => handleSelect(variant)}
            >
              <img
                src={`/images/colorswatches/swatch_${variant.attributes?.colour
                  .replaceAll(" ", "")
                  .replaceAll("/", "")}.png`}
                alt={variant.attributes?.colour}
                className="w-16 h-16 rounded-full mr-2"
              />
              <span className="text-gray-700 text-14">{getVariantName(variant)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default ColorSwatchesDropdown;
